// src/components/modals/ProfileModal.jsx
import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Modal from '../common/Modal';
import ChatAvatar from '../ChatAvatar';
import { cleanUser } from '../../utils/userHelpers';
import { isValidAvatarFile, getAvatarErrorMessage } from '../../utils/fileHelpers';
import './ProfileModal.css';

const ProfileModal = ({
    isOpen,
    onClose,
    user,
    
    api,                          // передаём axios-инстанс
    clearFileCache,               // очистка кеша файлов
    onUserUpdate,                 // колбэк после сохранения
    loadChats,                    // перезагрузка чатов
    showNotification,
    children,                     // сюда передадим блок "Уведомления" из App.js
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
            setUsername(user.username || '');
            setEmail(user.email || '');
            
            let phoneValue = user.phone || '';
            if (phoneValue && !phoneValue.startsWith('+')) {
                phoneValue = '+' + phoneValue;
            }
            setPhone(phoneValue);         
            setAvatarPreview('');
            setAvatarFile(null);
        }
    }, [isOpen, user]);

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
         if (!file) return;
        if (!isValidAvatarFile(file)) {
            showNotification(getAvatarErrorMessage(file), 'warning');
            e.target.value = '';   // сбрасываем выбор
            return;
        }
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const handleSave = async () => {
        if (phone) {
            const digits = phone.replace(/\D/g, '');
            if (digits.length < 10 || digits.length > 15) {
                showNotification('Неверный формат номера телефона', 'warning');
                return;
            }
        }

        setLoading(true);
        try {
            await api.put('/user/profile', {
                first_name: firstName,
                last_name: lastName,
                phone: phone,
                username: username,
            });

            if (avatarFile) {
                const formData = new FormData();
                formData.append('avatar', avatarFile);
                
                await api.post('/user/avatar', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            const response = await api.get('/user/profile');
            if (clearFileCache) clearFileCache();
            if (onUserUpdate) onUserUpdate(cleanUser(response.data));

            setAvatarFile(null);
            if (avatarPreview && avatarPreview.startsWith('blob:')) {
                URL.revokeObjectURL(avatarPreview);
            }
            setAvatarPreview('');
            
            if (loadChats) loadChats();
            onClose();
        } catch (error) {
            console.error('Failed to save profile:', error);
            showNotification('Ошибка сохранения профиля', 'warning');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Редактирование профиля"
            size="medium"
            footer={
                <>
                    <button className="btn-cancel" onClick={onClose}>Отмена</button>
                    <button 
                        className="btn-save" 
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </>
            }
        >
            <div className="profile-avatar-section">
                {avatarPreview ? (
                    <div className="avatar-preview">
                        <img src={avatarPreview} alt="Avatar" className="avatar-img" />
                    </div>
                ) : (
                    <ChatAvatar
                        chat={{
                            avatar: user?.avatar_url,
                            title: `${firstName} ${lastName}`.trim() || username || 'User',
                            chat_id: user?.id || 0,
                        }}
                        className="avatar-preview"
                        imgClassName="avatar-img"
                        placeholderClassName="avatar-placeholder"
                    />
                )}
                <label className="avatar-upload-btn">
                    <i className="bi bi-camera"></i>
                    {user?.avatar_url ? 'Изменить аватар' : 'Загрузить аватар'}
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg"
                        onChange={handleAvatarChange}
                        hidden
                    />
                </label>
            </div>

            <div className="profile-fields">
                <div className="profile-field">
                    <div className="readonly-text centered-text">
                        <i className="bi bi-envelope"></i> {email || 'Email не указан'}
                    </div>
                </div>

                <div className="profile-field">
                    <input
                        className="profile-input"
                        type="text"
                        placeholder="Логин"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        maxLength={100}
                    />
                </div>

                <div className="profile-field">
                    <PhoneInput
                        international
                        defaultCountry="RU"
                        value={phone}
                        onChange={setPhone}
                        limitMaxLength={true}
                        className="phone-input"
                    />
                </div>

                <div className="profile-row">
                    <div className="profile-field half">
                        <input
                            className="profile-input"
                            type="text"
                            placeholder="Имя"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            maxLength={100}
                        />
                    </div>
                    <div className="profile-field half">
                        <input
                            className="profile-input"
                            type="text"
                            placeholder="Фамилия"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            maxLength={100}
                        />
                    </div>
                </div>

                {/* Блок уведомлений (передаётся из App.js) */}
                {children}
            </div>
        </Modal>
    );
};

export default ProfileModal;